import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import type { AddOnRecord, ServiceItemRecord, ServiceRecord } from '../../db/types';
import {
  incrementAddOnQuantity,
  MAX_ADD_ON_QUANTITY,
  PricingEngine,
  setAddOnQuantity,
  type SelectedAddOn,
  type SelectedServiceItem,
} from '../../domain/pricing';
import { serviceItemRepository } from '../../db/repositories';

export type ConfiguredLine = {
  service: ServiceRecord;
  quantity: number;
  weightKg?: number;
  items: SelectedServiceItem[];
  addOns: SelectedAddOn[];
};
type Props = {
  service: ServiceRecord;
  addons: AddOnRecord[];
  onAdd: (line: ConfiguredLine) => void;
  onClose: () => void;
};

function decimalWeightInput(value: string): string {
  const cleaned = value.replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const dot = cleaned.indexOf('.');
  return dot < 0 ? cleaned : cleaned.slice(0, dot + 1) + cleaned.slice(dot + 1).replace(/\./g, '');
}

export function ServiceConfigurator({ service, addons, onAdd, onClose }: Props) {
  const [weight, setWeight] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [items, setItems] = useState<ServiceItemRecord[]>([]);
  const [selectedItems, setSelectedItems] = useState<Record<string, number>>({});
  const [selectedAddons, setSelectedAddons] = useState<SelectedAddOn[]>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (service.pricingType === 'PER_ITEM')
      void serviceItemRepository.listByServiceId(service.id).then(setItems);
  }, [service]);
  const selections: SelectedServiceItem[] = useMemo(
    () =>
      items.flatMap((item) => {
        const quantity = selectedItems[item.id] ?? 0;
        return quantity > 0 ? [{ item, quantity }] : [];
      }),
    [items, selectedItems],
  );
  const addOnSelections = useMemo(
    () => PricingEngine.normalizeAddOns(selectedAddons),
    [selectedAddons],
  );
  const weightKg = weight === '' ? undefined : Number(weight);
  const result = useMemo(() => {
    try {
      return PricingEngine.calculate({
        service,
        weightKg,
        quantity,
        items: selections,
        addOns: addOnSelections,
      });
    } catch {
      return null;
    }
  }, [service, weightKg, quantity, selections, addOnSelections]);

  const submit = () => {
    try {
      const calculated = PricingEngine.calculate({
        service,
        weightKg,
        quantity,
        items: selections,
        addOns: addOnSelections,
      });
      if (calculated.grandTotalCentavos <= 0)
        throw new Error('Please enter the required weight or select at least one laundry item.');
      onAdd({ service, quantity, weightKg, items: selections, addOns: addOnSelections });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid service details.');
    }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/40 p-2 sm:p-5">
      <div className="flex max-h-[calc(100dvh-1rem)] w-full max-w-3xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:max-h-[92dvh]">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-[#00549C]">
              Configure service
            </p>
            <h2 className="text-xl font-black text-[#003B70]">{service.name}</h2>
            <p className="text-xs font-semibold text-slate-400">
              {service.pricingType.replace('_', ' ')}
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close service details"
            className="grid h-14 w-14 place-items-center rounded-xl bg-slate-100"
          >
            <X />
          </button>
        </div>
        <div className="min-h-0 overflow-auto p-4 sm:p-6">
          {(service.pricingType === 'PER_KG' || service.pricingType === 'PER_LOAD') && (
            <div>
              <label htmlFor="weight-kg" className="block text-sm font-bold text-slate-700">
                Weight (KG)
                {service.pricingType === 'PER_LOAD' && service.loadCapacityKg ? (
                  <span className="ml-2 text-xs font-semibold text-slate-400">
                    Up to {service.loadCapacityKg} KG per load
                  </span>
                ) : null}
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  id="weight-kg"
                  aria-label="Weight (KG)"
                  autoFocus
                  type="tel"
                  inputMode="tel"
                  autoComplete="off"
                  enterKeyHint="done"
                  pattern="[0-9]*[.]?[0-9]*"
                  value={weight}
                  onChange={(e) => setWeight(decimalWeightInput(e.target.value))}
                  placeholder="0.000"
                  className="h-16 min-w-0 flex-1 rounded-xl border border-slate-200 px-4 text-2xl font-black outline-none focus:border-[#00549C]"
                />
                <button
                  type="button"
                  aria-label="Enter decimal point"
                  onClick={() =>
                    setWeight((current) => (current.includes('.') ? current : `${current || '0'}.`))
                  }
                  className="h-16 w-16 shrink-0 rounded-xl border border-slate-200 bg-[#F4F9FE] text-3xl font-black text-[#00549C]"
                >
                  .
                </button>
              </div>
            </div>
          )}
          {(service.pricingType === 'FIXED' || service.pricingType === 'PER_LOAD') && (
            <div>
              <p className="text-sm font-bold text-slate-700">
                {service.pricingType === 'PER_LOAD' ? 'Number of loads' : 'Quantity'}
              </p>
              <div className="mt-2 flex items-center gap-3">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="h-16 w-16 rounded-xl border text-2xl font-black"
                >
                  −
                </button>
                <span className="w-16 text-center text-2xl font-black">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="h-16 w-16 rounded-xl border text-2xl font-black"
                >
                  +
                </button>
              </div>
            </div>
          )}
          {service.pricingType === 'PER_ITEM' && (
            <div>
              <p className="mb-3 text-sm font-bold text-slate-700">Laundry items</p>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {items.map((item) => {
                  const count = selectedItems[item.id] ?? 0;
                  return (
                    <div
                      key={item.id}
                      className={`rounded-xl border p-3 ${count ? 'border-[#00549C] bg-[#DCEEFF]' : 'border-slate-200 bg-white'}`}
                    >
                      <button
                        onClick={() =>
                          setSelectedItems((current) => ({ ...current, [item.id]: count ? 0 : 1 }))
                        }
                        className="w-full text-left"
                      >
                        <p className="font-extrabold text-slate-800">{item.name}</p>
                        <p className="mt-1 text-sm font-bold text-[#00549C]">
                          {PricingEngine.formatCentavos(item.unitPriceCentavos)}
                        </p>
                      </button>
                      {count > 0 && (
                        <div className="mt-3 flex items-center justify-between">
                          <button
                            onClick={() =>
                              setSelectedItems((current) => ({
                                ...current,
                                [item.id]: Math.max(0, count - 1),
                              }))
                            }
                            className="h-10 w-10 rounded-lg bg-white font-black"
                          >
                            −
                          </button>
                          <span className="font-black">{count}</span>
                          <button
                            onClick={() =>
                              setSelectedItems((current) => ({ ...current, [item.id]: count + 1 }))
                            }
                            className="h-10 w-10 rounded-lg bg-white font-black"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {service.allowsAddons !== false && addons.length > 0 && (
            <div className="mt-6">
              <p className="mb-3 text-sm font-bold text-slate-700">Add-ons</p>
              <div className="grid grid-cols-2 gap-3">
                {addons.map((addon) => {
                  const selected = addOnSelections.find(
                    (selection) => selection.addOn.id === addon.id,
                  );
                  const addonQuantity = selected?.quantity ?? 0;
                  return (
                    <div key={addon.id} className="rounded-xl border border-slate-200 p-2">
                      <button
                        type="button"
                        aria-pressed={addonQuantity > 0}
                        onClick={() =>
                          setSelectedAddons((current) => incrementAddOnQuantity(current, addon))
                        }
                        className={`min-h-16 w-full rounded-lg border px-3 text-left ${addonQuantity > 0 ? 'border-[#00549C] bg-[#DCEEFF]' : 'border-slate-200 bg-white'}`}
                      >
                        <span className="block font-extrabold text-slate-800">{addon.name}</span>
                        <span className="text-xs font-bold text-[#00549C]">
                          {PricingEngine.formatCentavos(addon.priceCentavos)} per{' '}
                          {addon.pricingType.toLowerCase().replace('_', ' ')}
                        </span>
                      </button>
                      {addonQuantity > 0 && (
                        <div className="mt-2 flex items-center justify-center gap-3">
                          <button
                            type="button"
                            aria-label={`Decrease ${addon.name} quantity`}
                            onClick={() =>
                              setSelectedAddons((current) =>
                                setAddOnQuantity(current, addon, addonQuantity - 1),
                              )
                            }
                            className="grid h-16 w-16 min-h-16 min-w-16 place-items-center rounded-xl bg-slate-100 text-3xl font-black text-[#003B70]"
                          >
                            −
                          </button>
                          <span
                            aria-live="polite"
                            aria-label={`${addon.name} quantity`}
                            className="min-w-10 text-center text-2xl font-black tabular-nums text-[#003B70]"
                          >
                            {addonQuantity}
                          </span>
                          <button
                            type="button"
                            aria-label={`Increase ${addon.name} quantity`}
                            disabled={addonQuantity >= MAX_ADD_ON_QUANTITY}
                            onClick={() =>
                              setSelectedAddons((current) =>
                                setAddOnQuantity(current, addon, addonQuantity + 1),
                              )
                            }
                            className="grid h-16 w-16 min-h-16 min-w-16 place-items-center rounded-xl bg-[#00549C] text-3xl font-black text-white disabled:opacity-40"
                          >
                            +
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {error && (
            <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-bold text-red-600">{error}</p>
          )}
        </div>
        <div className="flex items-center justify-between gap-4 border-t border-slate-100 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-5">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Line total</p>
            <p className="text-3xl font-black text-[#003B70]">
              {result ? PricingEngine.formatCentavos(result.grandTotalCentavos) : '—'}
            </p>
          </div>
          <button
            onClick={submit}
            className="min-h-[64px] rounded-xl bg-[#00549C] px-8 text-lg font-black text-white"
          >
            Add to Order
          </button>
        </div>
      </div>
    </div>
  );
}
